/**
 * Candidate profile (v3 CandidateProfileView): pill tab switcher + Profile /
 * Resume / Activity Logs. Shared by the project candidate page, the Smart
 * Scout candidate modal (`layout="modal"`) and the Discover Talent profile
 * page (`variant="discover"`).
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  CandidateDetail,
  CandidateStatusEnum,
  MyCandidateFromAPI,
} from "../../types/candidate";
import { CandidateStatusEnum as CandidateStatusValues } from "../../types/candidate";
import { env } from "../../lib/env";
import { cn, toTitleCase } from "../../lib/utils";
import { Card } from "../ui/Card";
import { Avatar } from "../ui/Avatar";
import { Spinner } from "../ui/Spinner";
import { SegmentedControl } from "../ui/SegmentedControl";
import LetterLinear from "~icons/solar/letter-linear";
import PhoneLinear from "~icons/solar/phone-linear";
import MapPointLinear from "~icons/solar/map-point-linear";
import Buildings2Linear from "~icons/solar/buildings-2-linear";
import LinkCircleLinear from "~icons/solar/link-circle-linear";
import AddCircleLinear from "~icons/solar/add-circle-linear";
import BookmarkLinear from "~icons/solar/bookmark-linear";
import UserLinear from "~icons/solar/user-linear";
import UserBold from "~icons/solar/user-bold";
import DocumentTextLinear from "~icons/solar/document-text-linear";
import DocumentTextBold from "~icons/solar/document-text-bold";
import HistoryLinear from "~icons/solar/history-linear";
import HistoryBold from "~icons/solar/history-bold";
import { ensureHttps } from "./candidate-utils";
import {
  ScoreDisplay,
  StatusBadge,
  AttributesSection,
  MatchToJobCard,
  WorkExperienceCard,
  EducationCard,
  AwardsCard,
  AnimatedSection,
  ResumeTemplate,
} from "./shared-candidate-components";
import { ActivitiesLog, useActivityTrail } from "./ActivitiesLog";
import type { ActivityLogScope } from "../../hooks/useActivityLogs";
import { GreetingPreviewModal } from "./GreetingPreviewModal";
import { CandidateStatusDropdown } from "./CandidateStatusDropdown";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CandidateProfileVariant = "project" | "discover";

export interface CandidateProfileViewProps {
  data: CandidateDetail;
  candidateId: string;
  /** "project": Contact Now + status + Match to JD. "discover": Add to Project / Save to Pool + profile summary. */
  variant: CandidateProfileVariant;
  /** "modal" renders the tab pills in a white strip above a grey body (v3 CandidatesModal). */
  layout?: "page" | "modal";
  className?: string;

  /** Project variant: list-level row (project_id, CandidateStatus doc id, score). */
  candidate?: MyCandidateFromAPI;
  onContactAction?: (message: string) => void;
  onStatusChange?: (newStatus: CandidateStatusEnum) => void;
  isStatusUpdating?: boolean;

  /** Discover variant */
  onAddToProject?: () => void;
  onSaveToPool?: () => void;
  /** Discover variant: AI profile summary (generated when the record has none). */
  summary?: string | null;
  summaryLoading?: boolean;
  summaryError?: boolean;
}

type TabId = "profile" | "resume" | "activities";

// ---------------------------------------------------------------------------
// Details list row (v3 Candidate Details card)
// ---------------------------------------------------------------------------

function DetailItem({
  icon,
  label,
  value,
  href,
  breakAll = false,
}: {
  icon: ReactNode;
  label: string;
  value: string | undefined;
  href?: string;
  /** Email addresses have no break points — wrap anywhere (v3 break-all). */
  breakAll?: boolean;
}) {
  const displayValue = value || "N/A";

  return (
    <div>
      <span className="flex items-center gap-1.5 text-[11px] font-medium text-ink-faint">
        {icon}
        <span>{label}</span>
      </span>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-0.5 inline-block text-xs font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {displayValue} ↗
        </a>
      ) : (
        <p className={cn("mt-0.5 text-xs font-semibold text-ink", breakAll ? "break-all" : "break-words")}>
          {displayValue}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact Now (project variant) — opens the email modal directly (v3)
// ---------------------------------------------------------------------------

function ContactNowButton({
  candidateId,
  projectId,
  candidateName,
  candidateEmail,
  canContact,
  onContactAction,
}: {
  candidateId: string;
  projectId: string;
  candidateName: string;
  candidateEmail?: string;
  canContact: boolean;
  onContactAction: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const enabled = canContact && !!candidateEmail;

  return (
    <>
      <button
        type="button"
        disabled={!enabled}
        onClick={() => setOpen(true)}
        title={
          !candidateEmail
            ? "No email address on file"
            : !canContact
              ? "Outreach is available while the candidate is Uncontacted or Contacted"
              : undefined
        }
        className={cn(
          "mt-4 inline-flex h-9 min-w-[140px] items-center justify-center rounded-full px-6 text-xs font-bold text-white shadow-xs transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
          enabled
            ? "cursor-pointer bg-primary hover:scale-[1.02] hover:bg-primary-hover active:scale-[0.98]"
            : "cursor-not-allowed bg-ink-faint opacity-50"
        )}
      >
        Contact Now
      </button>

      <GreetingPreviewModal
        open={open}
        onClose={() => setOpen(false)}
        candidateId={candidateId}
        candidateName={candidateName}
        candidateEmail={candidateEmail}
        projectId={projectId}
        onSuccess={(msg) => {
          setOpen(false);
          void queryClient.invalidateQueries({ queryKey: ["projects", projectId, "candidates"] });
          void queryClient.invalidateQueries({ queryKey: ["candidates", candidateId] });
          void queryClient.invalidateQueries({ queryKey: ["activity-logs"] });
          onContactAction(msg);
        }}
        onError={onContactAction}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Left column: hero card + details + attributes
// ---------------------------------------------------------------------------

function ProfileSidebar({
  data,
  candidateId,
  variant,
  candidate,
  onContactAction,
  onStatusChange,
  isStatusUpdating,
  onAddToProject,
  onSaveToPool,
}: Pick<
  CandidateProfileViewProps,
  | "data"
  | "candidateId"
  | "variant"
  | "candidate"
  | "onContactAction"
  | "onStatusChange"
  | "isStatusUpdating"
  | "onAddToProject"
  | "onSaveToPool"
>) {
  const name = toTitleCase(data.full_name);
  const company = data.employment_history_items.find((e) => e.is_current)?.company_name
    ?? data.employment_history_items[0]?.company_name;

  // Project variant: prefer the fresh status from the detail response over the list row.
  const displayStatus = data.status ?? candidate?.status ?? CandidateStatusValues.NEW;
  const scoreSource = data.initial_confidence_score ?? candidate?.confidence_score;
  const initialScore = scoreSource ? Math.round(scoreSource.overall_score) : undefined;

  return (
    <div className="flex flex-col gap-5">
      {/* Hero: avatar, name, role, company + CTA */}
      <Card padding="lg" className="flex flex-col items-center rounded-4xl text-center">
        <Avatar
          name={name}
          src={data.avatar_url || candidate?.avatar_url}
          size="2xl"
          className="bg-primary-light text-2xl tracking-wider text-primary ring-2 ring-primary/25 shadow-card"
        />
        <div className="mt-4">
          <h2 className="text-lg font-bold text-ink">{name}</h2>
          {data.current_job_title && (
            <p className="mt-0.5 text-xs font-medium text-ink-muted">
              {toTitleCase(data.current_job_title)}
            </p>
          )}
          {company && (
            <div className="mt-1 flex items-center justify-center gap-1.5 text-xs font-medium text-ink-faint">
              <Buildings2Linear aria-hidden="true" className="size-[13px]" />
              <span>{toTitleCase(company)}</span>
            </div>
          )}
        </div>

        {variant === "discover" ? (
          <div className="mt-4 flex w-full flex-col gap-2.5 pt-2">
            <button
              type="button"
              onClick={onAddToProject}
              className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary px-6 text-xs font-bold text-white shadow-xs transition-all hover:scale-[1.01] hover:bg-primary-hover active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <AddCircleLinear aria-hidden="true" className="size-4" />
              <span>Add to Project</span>
            </button>
            <button
              type="button"
              onClick={onSaveToPool}
              className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-black/15 bg-white px-6 text-xs font-semibold text-ink shadow-2xs transition-all hover:border-black/25 hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <BookmarkLinear aria-hidden="true" className="size-[15px] text-ink-muted" />
              <span>Save to Pool</span>
            </button>
          </div>
        ) : (
          <>
            {candidate && (
              <ContactNowButton
                candidateId={candidateId}
                projectId={candidate.project_id}
                candidateName={name}
                candidateEmail={data.email ?? candidate.email}
                canContact={
                  displayStatus === CandidateStatusValues.NEW ||
                  displayStatus === CandidateStatusValues.CONTACTED
                }
                onContactAction={onContactAction ?? (() => {})}
              />
            )}

            <div className="mt-4 flex w-full flex-col gap-2.5 border-t border-black/[0.06] pt-4 text-left text-xs">
              <div className="flex items-center justify-between gap-3">
                <span className="text-ink-muted">Status</span>
                {onStatusChange ? (
                  <CandidateStatusDropdown
                    value={displayStatus}
                    onChange={onStatusChange}
                    isUpdating={isStatusUpdating ?? false}
                    size="sm"
                  />
                ) : (
                  <StatusBadge status={displayStatus} />
                )}
              </div>
              {env.FEATURE_CONFIDENCE_SCORE_UI && (
                <ScoreDisplay label="Confidence Score" score={initialScore} />
              )}
            </div>
          </>
        )}
      </Card>

      {/* Candidate Details */}
      <Card padding="lg" className="rounded-4xl">
        <h3 className="border-b border-black/[0.06] pb-3 text-xs font-bold uppercase tracking-wider text-ink">
          Candidate Details
        </h3>
        <div className="mt-4 flex flex-col gap-3.5">
          <DetailItem
            icon={<LetterLinear aria-hidden="true" className="size-3.5" />}
            label="Email"
            value={data.email}
            breakAll
          />
          <DetailItem
            icon={<PhoneLinear aria-hidden="true" className="size-3.5" />}
            label="Phone"
            value={data.phone}
          />
          <DetailItem
            icon={<LinkCircleLinear aria-hidden="true" className="size-3.5" />}
            label="LinkedIn URL"
            value={data.linkedin_profile_url ? "View Profile" : undefined}
            href={data.linkedin_profile_url ? ensureHttps(data.linkedin_profile_url) : undefined}
          />
          <DetailItem
            icon={<MapPointLinear aria-hidden="true" className="size-3.5" />}
            label="Location"
            value={data.address}
          />
          <DetailItem
            icon={<Buildings2Linear aria-hidden="true" className="size-3.5" />}
            label="Company"
            value={company ? toTitleCase(company) : undefined}
          />
        </div>
      </Card>

      {/* Attributes */}
      <Card padding="lg" className="rounded-4xl">
        <AttributesSection items={data.skills} />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export function CandidateProfileView({
  data,
  candidateId,
  variant,
  layout = "page",
  className,
  candidate,
  onContactAction,
  onStatusChange,
  isStatusUpdating,
  onAddToProject,
  onSaveToPool,
  summary,
  summaryLoading = false,
  summaryError = false,
}: CandidateProfileViewProps) {
  const [activeTab, setActiveTab] = useState<TabId>("profile");
  const name = toTitleCase(data.full_name);

  const scope: ActivityLogScope =
    variant === "discover"
      ? { candidateId }
      : { candidateStatusId: candidate?.id ?? data.candidate_status_id };
  const { total: activityCount } = useActivityTrail(scope);

  // Right-column summary card + resume executive summary
  const matchSummary =
    variant === "discover"
      ? (summary ?? data.profile_summary ?? null)
      : (data.profile_matching_summary ?? candidate?.profile_matching_summary ?? null);
  const resumeSummary = data.profile_summary ?? summary ?? matchSummary;

  const summaryPlaceholder =
    variant === "discover" && summaryLoading ? (
      <div className="flex items-center gap-2 text-xs text-ink-muted">
        <Spinner size={14} />
        <span>Generating AI summary…</span>
      </div>
    ) : variant === "discover" && summaryError ? (
      <p className="text-xs text-error">Failed to generate AI summary. Please try again later.</p>
    ) : undefined;

  return (
    <div
      className={cn(layout === "modal" ? "flex flex-col" : "flex flex-col gap-6", className)}
      role="region"
      aria-label={`${name} details`}
    >
      {/* Tab switcher — white strip in the modal (v3), inline on the page */}
      <div
        className={
          layout === "modal"
            ? "flex items-center border-b border-black/[0.06] bg-white px-6 py-3"
            : "flex items-center justify-between"
        }
      >
        <SegmentedControl<TabId>
          pill
          aria-label="Candidate profile tabs"
          items={[
            { value: "profile", label: "Profile", icon: UserLinear, activeIcon: UserBold },
            { value: "resume", label: "Resume", icon: DocumentTextLinear, activeIcon: DocumentTextBold },
            {
              value: "activities",
              label: "Activity Logs",
              icon: HistoryLinear,
              activeIcon: HistoryBold,
              count: activityCount > 0 ? activityCount : undefined,
              countTone: activeTab === "activities" ? "primary" : "muted",
            },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          className="self-start shadow-2xs"
        />
      </div>

      {/* Tab panel */}
      <div
        id={`tabpanel-${activeTab}`}
        role="tabpanel"
        className={layout === "modal" ? "flex-1 bg-surface-2 p-6" : "flex-1"}
      >
        {activeTab === "profile" && (
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
            {/* LEFT: avatar, CTA, details, attributes */}
            <div className="lg:col-span-4">
              <AnimatedSection index={0}>
                <ProfileSidebar
                  data={data}
                  candidateId={candidateId}
                  variant={variant}
                  candidate={candidate}
                  onContactAction={onContactAction}
                  onStatusChange={onStatusChange}
                  isStatusUpdating={isStatusUpdating}
                  onAddToProject={onAddToProject}
                  onSaveToPool={onSaveToPool}
                />
              </AnimatedSection>
            </div>

            {/* RIGHT: summary, experience, education, awards */}
            <div className="flex min-w-0 flex-col gap-6 lg:col-span-8">
              <AnimatedSection index={1}>
                <MatchToJobCard
                  title={variant === "discover" ? "Profile Summary & Highlights" : "Match to Job Description"}
                  content={matchSummary}
                  placeholder={summaryPlaceholder}
                />
              </AnimatedSection>
              <AnimatedSection index={2}>
                <WorkExperienceCard items={data.employment_history_items} />
              </AnimatedSection>
              <AnimatedSection index={3}>
                <EducationCard items={data.education_items} />
              </AnimatedSection>
              <AnimatedSection index={4}>
                <AwardsCard items={data.awards ?? []} />
              </AnimatedSection>
            </div>
          </div>
        )}

        {activeTab === "resume" && (
          <ResumeTemplate data={data} candidateName={name} summary={resumeSummary} />
        )}

        {activeTab === "activities" && <ActivitiesLog scope={scope} />}
      </div>
    </div>
  );
}
